import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/shared/api/api-error';

import { useChangePassword } from '../api/account.queries';
import {
  changePasswordSchema,
  type ChangePasswordFormValues,
} from '../schemas/change-password.schema';

const FIELDS = ['currentPassword', 'newPassword', 'confirmPassword'] as const;

/**
 * The fields and submit button shared by the forced change-password screen
 * and the account-menu dialog. Server field errors (INCORRECT_PASSWORD on
 * currentPassword, WEAK_PASSWORD on newPassword) land on their fields.
 */
export function ChangePasswordForm({
  onDone,
  onCancel,
  submitLabel = 'Change password',
}: {
  onDone: () => void;
  onCancel?: () => void;
  submitLabel?: string;
}) {
  const mutation = useChangePassword();
  const form = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const { error } = mutation;
  useEffect(() => {
    if (!(error instanceof ApiError) || !error.fieldErrors) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      const key = FIELDS.find((name) => name.toLowerCase() === field.toLowerCase());
      if (key) form.setError(key, { type: 'server', message });
    }
  }, [error, form]);

  const formError =
    error instanceof ApiError && !error.fieldErrors
      ? error.message
      : error && !(error instanceof ApiError)
        ? 'Could not change the password.'
        : null;
  const isPending = mutation.isPending;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((values) => mutation.mutate(values, { onSuccess: onDone }))}
        className="space-y-4"
        noValidate
      >
        <FormField
          control={form.control}
          name="currentPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Current password</FormLabel>
              <FormControl>
                <Input {...field} type="password" autoComplete="current-password" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="newPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>New password</FormLabel>
              <FormControl>
                <Input {...field} type="password" autoComplete="new-password" />
              </FormControl>
              <FormDescription>At least 10 characters, with a letter and a digit.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Confirm new password</FormLabel>
              <FormControl>
                <Input {...field} type="password" autoComplete="new-password" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {formError && (
          <p role="alert" className="text-destructive text-xs">
            {formError}
          </p>
        )}
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isPending}>
            {isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
            {submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}
